import { useEffect, useState } from 'preact/hooks';
import { useI18n } from '../i18n';

type Props = {
  open: boolean;
  storeName: string | null;
  onClose: () => void;
};

export function StoreModal({ open, storeName, onClose }: Props) {
  const { t } = useI18n();
  const [saved, setSaved] = useState(false);
  const [savedEmail, setSavedEmail] = useState('');

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) {
      setSaved(false);
      setSavedEmail('');
      setError(null);
      setSubmitting(false);
    }
  }, [open]);

  async function onSubmit(e: Event) {
    e.preventDefault();
    const form = e.currentTarget as HTMLFormElement;
    const input = form.querySelector('input[type=email]') as HTMLInputElement | null;
    const hp = form.querySelector('input[name=website]') as HTMLInputElement | null;
    const email = input?.value.trim() ?? '';
    const website = hp?.value ?? '';
    if (!email) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch('/api/waitlist', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, source: storeName ?? '', locale: undefined, website }),
      });
      if (!res.ok) throw new Error(String(res.status));
      setSavedEmail(email);
      setSaved(true);
    } catch {
      setError(t('modal.error'));
    } finally {
      setSubmitting(false);
    }
  }

  const isWaitlist = storeName === 'Waitlist';

  return (
    <div
      class={`modal-backdrop${open ? ' open' : ''}`}
      role="dialog"
      aria-modal="true"
      aria-labelledby="modalTitle"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div class={`modal${saved ? ' is-saved' : ''}`}>
        <button class="modal-close" aria-label={t('modal.close')} onClick={onClose}>
          ×
        </button>

        {saved ? (
          <div class="modal-success">
            <div class="success-glass" aria-hidden="true">
              <span class="liquid" />
              <span class="foam" />
              <span class="bubbles">
                <span /><span /><span /><span /><span />
              </span>
              <svg class="check" viewBox="0 0 52 52" aria-hidden="true">
                <path d="M14 27l8 8 16-18" fill="none" stroke="currentColor" stroke-width="5" stroke-linecap="round" stroke-linejoin="round" />
              </svg>
            </div>
            <h3 id="modalTitle">
              {t('modal.success_title_a')} <em>{t('modal.success_title_b')}</em>
            </h3>
            <p class="sent-to">
              <span class="sent-to-label">{t('modal.sent_to_label')}</span>
              <span class="sent-to-email">{savedEmail}</span>
            </p>
            <p class="check-inbox">{t('modal.check_inbox')}</p>
            <button type="button" class="success-done" onClick={onClose}>
              {t('modal.done')}
            </button>
          </div>
        ) : (
          <>
            <div class="modal-glass" />
            <h3 id="modalTitle">
              {t('modal.title_a')} <em>{t('modal.title_b')}</em>
            </h3>
            <p>
              {isWaitlist ? (
                t('modal.body_waitlist')
              ) : (
                <>
                  {t('modal.body_before')} <span>{storeName ?? 'App Store'}</span>
                  {t('modal.body_after')}
                </>
              )}
            </p>
            <span class="release">{t('modal.release')}</span>
            <form class="notify-form" onSubmit={onSubmit}>
              <input
                type="email"
                placeholder={t('modal.placeholder')}
                required
                disabled={submitting}
              />
              <div class="hp-field" aria-hidden="true">
                <label>
                  Website
                  <input type="text" name="website" tabIndex={-1} autoComplete="off" />
                </label>
              </div>
              <button type="submit" disabled={submitting}>
                {submitting ? t('modal.submitting') : t('modal.notify')}
              </button>
            </form>
            {error && <span class="notify-error">{error}</span>}
          </>
        )}
      </div>
    </div>
  );
}
